import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  TestsPageContainer,
  ContentWrapper,
  HeaderContainer,
  Title,
} from "./TestsPage.styles";
import Sidebar from "../../../components/Sidebar";
import Table from "../../../components/Table";
import IconButton from "../../../components/Buttons/IconButton";
import { Toast } from "primereact/toast";
import delIcon from "../../../assets/icons/delIcon.svg";
import editIcon from "../../../assets/icons/editIcon.svg";
import { Test } from "../../../services/documentService/documentService.types";
import { fetchTests, createTest, updateTest, deleteTest } from "../../../services/documentService/documentService";
import Button from "../../../components/Buttons/Button";
import { ButtonWrapper } from "../TemplatesPage.styles";
import { ConfirmModal, CreateModal } from "../../../components/Modals";
import DeletedRow from "../../../components/DeletedRow";
import RestoredRow from "../../../components/RestoredRow";
import BreadcrumbLink from "../../../components/BreadcrumbLink/BreadcrumbLink";
import { ButtonsContainer } from "../../DocumentVersionsPage/DocumentVersionsPage.styles";
import { formatServerErrors } from "../../../utils/errorHandler";

const TestsPage: React.FC = () => {
  const navigate = useNavigate();
  const [tests, setTests] = useState<Test[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(7);
  const [orderBy, setOrderBy] = useState<string>("");
  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [testToEdit, setTestToEdit] = useState<Test | null>(null);
  const [externalErrors, setExternalErrors] = useState<Record<string, string>>({});
  const toast = useRef<Toast>(null);

  // Состояния для подтверждения удаления
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [testToDelete, setTestToDelete] = useState<Test | null>(null);

  // Состояния для реализации отложенного удаления и уведомлений
  const [pendingDelete, setPendingDelete] = useState<number | null>(null);
  const [recentlyCreated, setRecentlyCreated] = useState<number | null>(null);
  const [recentlyEdited, setRecentlyEdited] = useState<number | null>(null);
  const [recentlyRestored, setRecentlyRestored] = useState<number | null>(null);
  const deleteTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const [searchParams] = useSearchParams();
  
    const documentName = searchParams.get("documentName") || "Неизвестный документ";
    const templateName = searchParams.get("templateName") || "Неизвестный шаблон";
    const { documentId } = useParams<{ documentId: string }>();
    const templateId = searchParams.get("templateId") || "0";

  useEffect(() => {
    if (!templateId) {
      toast.current?.show({
        severity: "error",
        summary: "Ошибка",
        detail: "Необходимо указать документ.",
      });
      navigate("/login");
      return;
    }
    loadTests();
  }, [templateId, currentPage, pageSize, orderBy]);

  const loadTests = async () => {
    if (!templateId) {
      toast.current?.show({
        severity: "error",
        summary: "Ошибка",
        detail: "Не удалось получить ID документа",
        life: 3000,
      });
      return;
    }
    setLoading(true);
    try {
      const response = await fetchTests(Number(documentId), currentPage, pageSize, orderBy);
      setTests(response.results);
      setTotalPages(Math.ceil(response.count / pageSize));
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Ошибка",
        detail: "Ошибка загрузки тестов",
        life: 3000,
      });
      console.error("Ошибка при загрузке тестов:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field: string) => {
    if (orderBy === field) {
      setOrderBy(`-${field}`);
    } else if (orderBy === `-${field}`) {
      setOrderBy("");
    } else {
      setOrderBy(field);
    }
  };

  const openDeleteModal = (test: Test) => {
    setTestToDelete(test);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setTestToDelete(null);
  };

  const confirmDelete = () => {
    if (testToDelete) {
      handleDelete(testToDelete.id);
      closeDeleteModal();
    }
  };

  // Отложенное удаление с задержкой в 5 секунд
  const handleDelete = (id: number) => {
    setPendingDelete(id);
    deleteTimeoutRef.current = setTimeout(async () => {
      try {
        await deleteTest(id);
        setPendingDelete(null);
        toast.current?.show({
          severity: "success",
          summary: "Удалено",
          detail: "Тест успешно удален",
          life: 2000,
        });
        loadTests();
      } catch (error) {
        toast.current?.show({
          severity: "error",
          summary: "Ошибка",
          detail: "Не удалось удалить тест",
          life: 3000,
        });
        console.error("Ошибка при удалении теста:", error);
        setPendingDelete(null);
      }
    }, 5000);
  };

  // Функция восстановления – теперь устанавливаем recentlyRestored
  const restoreTestHandler = () => {
    if (deleteTimeoutRef.current) {
      clearTimeout(deleteTimeoutRef.current);
      deleteTimeoutRef.current = null;
    }
    const restoredId = pendingDelete; // восстанавливаем именно тот, который был отложен
    setPendingDelete(null);
    setRecentlyRestored(restoredId);
    toast.current?.show({
      severity: "success",
      summary: "Восстановлено",
      detail: "Удаление теста отменено",
      life: 3000,
    });
    setTimeout(() => setRecentlyRestored(null), 3000);
  };

  const openCreateModal = () => {
    setTestToEdit(null);
    setIsCreateModalOpen(true);
  };

  const openEditModal = (test: Test) => {
    setTestToEdit(test);
    setIsCreateModalOpen(true);
  };

  const closeCreateModal = () => {
    setIsCreateModalOpen(false);
  };

  const handleCreateOrEditTest = async (formData: Record<string, string | File[] | null>) => {
    setExternalErrors({});
    try {
      if (testToEdit) {
        await updateTest(testToEdit.id, { name: formData["name"] as string, content: formData["content"] as string, version: documentId  });
        setRecentlyEdited(testToEdit.id);
        toast.current?.show({
          severity: "success",
          summary: "Успех",
          detail: "Тест обновлен",
          life: 3000,
        });
      } else {
        console.log("documentId", documentId);
        const createdTest = await createTest(Number(documentId), { name: formData["name"] as string, content: formData["content"] as string, version: documentId  });
        setRecentlyCreated(createdTest.id);
        toast.current?.show({
          severity: "success",
          summary: "Успех",
          detail: "Тест создан",
          life: 3000,
        });
      }
      closeCreateModal();
      loadTests();
      setTimeout(() => {
        setRecentlyCreated(null);
        setRecentlyEdited(null);
      }, 3000);
    } catch (error: any) {
      if (error.response && error.response.data) {
        const serverErrors = error.response.data;
        const formattedErrors = formatServerErrors(serverErrors);
        setExternalErrors(formattedErrors);
      } else {
        console.error("Ошибка при сохранении теста:", error);
        toast.current?.show({
          severity: "error",
          summary: "Ошибка",
          detail: "Ошибка сохранения теста",
          life: 3000,
        });
      }
    }
  };

  const headers = [
    { label: "Название теста", key: "name", fieldName: "name" },
    { label: "Содержание", key: "view", sortable: false },
    { label: "", key: "buttons", sortable: false, fieldName: "delete" },
  ];

  const rows = tests.map((test) => {
    if (test.id === pendingDelete) {
      return {
        name: (
          <DeletedRow
            id={test.id}
            onRestore={restoreTestHandler}
            colSpan={headers.length}
            message={`Тест "${test.name}" будет удален через 5 секунд.`}
            targetText="Отменить удаление"
          />
        ),
      };
    }
    return {
      name: test.name,
      view: (
        <div
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: "300px",
          }}
          title={test.content}
        >
          {test.content}
        </div>
      ),
      buttons: (
        <ButtonsContainer>
          <IconButton
            icon={editIcon}
            altText="Редактировать"
            onClick={() => openEditModal(test)}
          />
          <IconButton
            icon={delIcon}
            altText="Удалить"
            onClick={() => openDeleteModal(test)}
          />
        </ButtonsContainer>
      ),
    };
  });

  // Вставляем строки уведомлений, как в CoursesPage
  if (recentlyRestored !== null) {
    const restoredIndex = tests.findIndex((t) => t.id === recentlyRestored);
    if (restoredIndex !== -1) {
      rows.splice(restoredIndex + 1, 0, {
        name: (
          <RestoredRow colSpan={headers.length} message="Тест успешно восстановлен" />
        ),
      });
    }
  }
  if (recentlyCreated !== null) {
    const createdIndex = tests.findIndex((t) => t.id === recentlyCreated);
    if (createdIndex !== -1) {
      rows.splice(createdIndex + 1, 0, {
        name: (
          <RestoredRow colSpan={headers.length} message="Тест успешно создан" />
        ),
      });
    }
  }
  if (recentlyEdited !== null) {
    const editedIndex = tests.findIndex((t) => t.id === recentlyEdited);
    if (editedIndex !== -1) {
      rows.splice(editedIndex + 1, 0, {
        name: (
          <RestoredRow colSpan={headers.length} message="Тест успешно отредактирован" />
        ),
      });
    }
  }

  const rowAmountOptions = [
    { label: "7 записей", value: 7 },
    { label: "15 записей", value: 15 },
    { label: "20 записей", value: 20 },
  ];

  return (
    <TestsPageContainer>
      <Toast ref={toast} />
      <Sidebar />
      <ContentWrapper>
        {/* Хлебные крошки */}
        <div style={{ marginBottom: "16px" }}>
          <BreadcrumbLink href="/documents" text="Шаблоны документов" />
          <span style={{ margin: "0 8px" }}>/</span>
          <BreadcrumbLink
            href={`/documents/${templateId}/versions?templateName=${templateName}`}
            text={`Шаблон ${templateName}`}
          />
          <span style={{ margin: "0 8px" }}>/</span>
          <BreadcrumbLink
            href={`/versions/${documentId}/practices?documentName=${documentName}&templateId=${templateId}}&templateName=${templateName}`}
            text={`Тесты документа ${documentName}`}
          />
        </div>
        <HeaderContainer>
          <Title>Тесты</Title>
          <ButtonWrapper>
            <Button onClick={openCreateModal} variant="active">
              Добавить тест
            </Button>
          </ButtonWrapper>
        </HeaderContainer>
        <Table
          headers={headers}
          rows={rows}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
          rowAmountOptions={rowAmountOptions}
          loading={loading}
          minHeight="200px"
          onSort={handleSort}
          specialRowKey="name"
        />
      </ContentWrapper>
      {isCreateModalOpen && (
        <CreateModal
          title={testToEdit ? "Редактировать тест" : "Новый тест"}
          fields={[
            { name: "name", type: "text", placeholder: "Название теста", required: true },
            { name: "content", type: "textarea", placeholder: "Содержание", required: true },
          ]}
          onSubmit={handleCreateOrEditTest}
          submitButtonText={testToEdit ? "Сохранить" : "Создать"}
          onCancel={closeCreateModal}
          initialValues={testToEdit ? { name: testToEdit.name, content: testToEdit.content } : {}}
          externalErrors={externalErrors}
        />
      )}
      {isDeleteModalOpen && (
        <ConfirmModal
          isOpen={isDeleteModalOpen}
          title="Подтверждение удаления"
          message={`Вы уверены, что хотите удалить тест "${testToDelete?.name}"?`}
          onConfirm={confirmDelete}
          onCancel={closeDeleteModal}
          confirmButtonText="Удалить"
        />
      )}
    </TestsPageContainer>
  );
};

export default TestsPage;
